import React, { FC, ReactElement } from 'react';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import Accordion, {
  AccordionContent,
  AccordionTitle
} from '@buyside/components/presentation/Accordion/accordionComponents';
import ShowPageCommentContainer from '@buyside/components/container/ShowPageComments/ShowPageCommentContainer';
import WhitePaperCommentForm from '@buyside/components/presentation/WhitePaper/Comments/WhitePaperCommentForm';

import { useWhitePaperCommentsQuery } from '@interfaces/graphql-typings';

import LoadingSpinner from '../../LoadingSpinner/loadingSpinner';

import styles from './styles.scss';

const WhitePaperCommentList: FC<{
  authorId: string;
  authorName: string;
  canCommentAnonymously: boolean;
  canWriteWhitePaperComments: boolean;
  isAuthor: boolean;
  isAdmin: boolean;
  isEmployed: boolean;
  parentId?: string;
  profileImageUrl: string;
  whitePaperId: string;
}> = ({
  authorId,
  authorName,
  canCommentAnonymously,
  canWriteWhitePaperComments,
  isAuthor,
  isAdmin,
  isEmployed,
  profileImageUrl,
  whitePaperId
}): ReactElement => {
  const { loading, data } = useWhitePaperCommentsQuery({ variables: { id: whitePaperId } });

  if (loading || !data) return <LoadingSpinner />;

  const comments = data?.whitePaper?.comments?.edges;
  const commentCount = comments?.length;
  const allComments = comments?.map(e => e?.node || null) || [];

  const currentUserId = data.currentUser?.id;

  return (
    <div id="white-paper-comments" className={styles.container}>
      <Accordion basic initialOpen>
        <AccordionTitle expandable expandIcon={faPlus} collapseIcon={faMinus} dataTag="comments">
          Comments ({commentCount})
        </AccordionTitle>
        <AccordionContent>
          <>
            {canWriteWhitePaperComments && (
              <WhitePaperCommentForm
                canCommentAnonymously={Boolean(canCommentAnonymously)}
                currentUserId={currentUserId || ''}
                isAuthor={Boolean(isAuthor)}
                whitePaperId={whitePaperId}
              />
            )}
            <TransitionGroup>
              {comments
                ?.filter(e => !e?.node?.parentId)
                .map(edge => {
                  if (!edge) return null;

                  const comment = edge.node;

                  if (!comment) return null;

                  return (
                    <CSSTransition
                      key={`comment-${comment.id}`}
                      classNames="fade"
                      timeout={1000}
                      in
                      appear
                    >
                      <ShowPageCommentContainer
                        allComments={allComments}
                        authorId={authorId}
                        authorName={authorName}
                        canCommentAnonymously={Boolean(canCommentAnonymously)}
                        canViewUserDetails={Boolean(data?.currentUser?.canViewUserDetails)}
                        canWriteComments={Boolean(canWriteWhitePaperComments || isAuthor)}
                        comment={comment}
                        commentableId={whitePaperId}
                        commentableType="WhitePaper"
                        currentUserId={currentUserId}
                        isAdmin={Boolean(isAdmin)}
                        isAllocator={false}
                        isAuthor={Boolean(isAuthor)}
                        isEmployed={isEmployed}
                        isIdeaAnonymous={false}
                        isNonBuyside={false}
                        isIdeaClosed={false}
                        mentionables={[]}
                        profileImageUrl={profileImageUrl}
                      />
                    </CSSTransition>
                  );
                })}
            </TransitionGroup>
          </>
        </AccordionContent>
      </Accordion>
    </div>
  );
};

export default WhitePaperCommentList;
